/**
 * The side drawer both the information button and the audit button open.
 *
 * Rendered in a portal on document.body so that its position is fixed to the
 * viewport rather than to whatever card contained the button, and so that a
 * drawer opened from inside a scrolling table is not clipped by it. Escape
 * closes, the scrim closes, and focus moves to the close button on open: the
 * drawer carries the explanation of a funding decision, so it has to be
 * reachable and dismissable from the keyboard.
 */
import { useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'

export function Drawer({ title, open, onClose, children }: {
  title: string
  open: boolean
  onClose: () => void
  children: ReactNode
}) {
  const closeRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    closeRef.current?.focus()
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return createPortal(
    <>
      <div className="scrim on" onClick={onClose} />
      <aside className="drawer on" role="dialog" aria-modal="true"
             aria-label={title}>
        <header>
          <h3>{title}</h3>
          <div className="grow" />
          <button className="x" type="button" ref={closeRef} onClick={onClose}
                  aria-label="Close">×</button>
        </header>
        <div className="body">{children}</div>
      </aside>
    </>,
    document.body,
  )
}
